import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "./ThemeProvider";
import { dialogs } from "../context/DialogContext";
import { postCaptacionMultipart } from "../services/captacionUpload";

const CATEGORIAS = [
  { value: "error", label: "Error / fallo" },
  { value: "duda", label: "Duda" },
  { value: "mejora", label: "Solicitud de mejora" },
  { value: "otro", label: "Otro" },
];

const PRIORIDADES = [
  { value: "baja", label: "Baja" },
  { value: "media", label: "Media" },
  { value: "alta", label: "Alta" },
];

const MAX_ARCHIVOS = 6;
const MAX_MB_POR_ARCHIVO = 20;

const FORM_VACIO = { asunto: "", descripcion: "", categoria: "error", prioridad: "media" };

function iconoPorTipo(file) {
  if (file.type?.startsWith("image/")) return null; // se usa miniatura real
  if (file.type === "application/pdf") return "📄";
  return "📎";
}

function formatoTamano(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ModalNuevoTicket({ open, onClose }) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [form, setForm] = useState(FORM_VACIO);
  const [archivos, setArchivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      archivos.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  const cerrar = () => {
    archivos.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    setForm(FORM_VACIO);
    setArchivos([]);
    setEnviado(false);
    onClose();
  };

  const agregarArchivos = (fileList) => {
    const nuevos = Array.from(fileList || []);
    if (!nuevos.length) return;
    setArchivos((prev) => {
      const disponibles = MAX_ARCHIVOS - prev.length;
      if (disponibles <= 0) {
        dialogs.alert(`Solo se permiten hasta ${MAX_ARCHIVOS} archivos adjuntos`);
        return prev;
      }
      const aceptados = [];
      const rechazadosPeso = [];
      for (const f of nuevos.slice(0, disponibles)) {
        if (f.size > MAX_MB_POR_ARCHIVO * 1024 * 1024) {
          rechazadosPeso.push(f.name);
          continue;
        }
        aceptados.push({
          file: f,
          preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        });
      }
      if (rechazadosPeso.length) {
        dialogs.alert(`No se añadieron (superan ${MAX_MB_POR_ARCHIVO} MB): ${rechazadosPeso.join(", ")}`);
      }
      return [...prev, ...aceptados];
    });
  };

  const quitarArchivo = (idx) => {
    setArchivos((prev) => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const enviar = async () => {
    if (!form.asunto.trim()) {
      await dialogs.alert("Indica un asunto para el ticket");
      return;
    }
    if (!form.descripcion.trim()) {
      await dialogs.alert("Describe el problema o la solicitud");
      return;
    }
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("asunto", form.asunto.trim());
      fd.append("descripcion", form.descripcion.trim());
      fd.append("categoria", form.categoria);
      fd.append("prioridad", form.prioridad);
      fd.append("pagina_url", window.location.pathname);
      archivos.forEach((a) => fd.append("adjuntos", a.file));
      // Directo a Railway: el rewrite de Vercel corta multipart (capturas) → 502.
      const { data } = await postCaptacionMultipart("/tickets-soporte", fd);
      if (data?.email_aviso && data.email_aviso.ok === false) {
        await dialogs.alert(
          "Ticket creado, pero no se pudo avisar por email a soporte.\n\n" +
            (data.email_aviso.error ||
              "Configura tu buzón en Configuración → Mi correo (tu @vitalcasa.com y contraseña) para que el aviso salga de tu correo.")
        );
      }
      setEnviado(true);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.error ||
        (status === 502
          ? "El servidor no pudo recibir los adjuntos. Prueba con menos capturas o archivos más pequeños, o sin adjuntos."
          : null) ||
        e?.message ||
        "No se pudo enviar el ticket";
      await dialogs.alert(msg);
    } finally {
      setEnviando(false);
    }
  };

  const inpSt = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? "#111827" : "#f9fafb",
    border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
    color: isDark ? "#f9fafb" : "#111827",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const labelSt = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: t.textMuted,
    display: "block",
    marginBottom: 5,
  };

  const modal = (
    <div
      data-modal-form
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 600,
        fontFamily: "'DM Sans', sans-serif",
      }}
      onClick={cerrar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: t.bgCard,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          border: `1px solid ${t.border}`,
        }}
      >
        <div style={{ height: 3, flexShrink: 0, background: "linear-gradient(90deg, #f59e0b, #d97706, #b45309, #d97706, #f59e0b)" }} />
        <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                boxShadow: "0 4px 12px rgba(217,119,6,0.35)",
              }}
            >
              🎫
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.textPrimary }}>Ticket de soporte</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>
                Cuéntanos qué ha pasado, avisamos al equipo por email
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={cerrar}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              cursor: "pointer",
              fontSize: 16,
              color: t.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${t.border}, transparent)`, marginTop: 16 }} />

        {enviado ? (
          <div style={{ padding: "32px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: t.textPrimary }}>Ticket enviado</h3>
            <p style={{ margin: "8px 0 0", fontSize: 13, color: t.textMuted }}>
              Hemos recibido tu incidencia y hemos avisado al equipo por email.
            </p>
            <button
              type="button"
              onClick={cerrar}
              style={{
                marginTop: 20,
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "#fff",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 8px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={labelSt}>Asunto *</label>
                <input
                  value={form.asunto}
                  onChange={(e) => setForm((f) => ({ ...f, asunto: e.target.value }))}
                  placeholder="Resumen breve del problema"
                  style={inpSt}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelSt}>Categoría</label>
                  <select
                    value={form.categoria}
                    onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
                    style={inpSt}
                  >
                    {CATEGORIAS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Prioridad</label>
                  <select
                    value={form.prioridad}
                    onChange={(e) => setForm((f) => ({ ...f, prioridad: e.target.value }))}
                    style={inpSt}
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelSt}>Descripción *</label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Explica con el máximo detalle qué ha pasado, qué esperabas y en qué pantalla estabas"
                  style={{ ...inpSt, resize: "vertical", minHeight: 110, fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label style={labelSt}>Adjuntos (imágenes o documentos)</label>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    agregarArchivos(e.target.files);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={archivos.length >= MAX_ARCHIVOS}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: `1.5px dashed ${isDark ? "#374151" : "#cbd5e1"}`,
                    background: isDark ? "#111827" : "#f9fafb",
                    color: t.textMuted,
                    cursor: archivos.length >= MAX_ARCHIVOS ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: archivos.length >= MAX_ARCHIVOS ? 0.6 : 1,
                  }}
                >
                  📎 Añadir capturas o documentos
                </button>
                <p style={{ margin: "5px 0 0", fontSize: 11, color: t.textMuted }}>
                  Hasta {MAX_ARCHIVOS} archivos, {MAX_MB_POR_ARCHIVO} MB cada uno
                </p>
                {archivos.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                    {archivos.map((a, idx) => (
                      <div
                        key={idx}
                        style={{
                          position: "relative",
                          width: 72,
                          height: 72,
                          borderRadius: 8,
                          overflow: "hidden",
                          border: `1px solid ${t.border}`,
                          background: isDark ? "#111827" : "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                        title={`${a.file.name} (${formatoTamano(a.file.size)})`}
                      >
                        {a.preview ? (
                          <img src={a.preview} alt={a.file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <>
                            <span style={{ fontSize: 22 }}>{iconoPorTipo(a.file)}</span>
                            <span style={{ fontSize: 9, color: t.textMuted, padding: "0 4px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                              {a.file.name}
                            </span>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => quitarArchivo(idx)}
                          title="Quitar"
                          style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "none",
                            background: "rgba(0,0,0,0.65)",
                            color: "#fff",
                            fontSize: 11,
                            lineHeight: "18px",
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                padding: "16px 24px 20px",
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                flexShrink: 0,
                borderTop: `1px solid ${t.border}`,
              }}
            >
              <button
                type="button"
                onClick={cerrar}
                disabled={enviando}
                style={{
                  padding: "11px 24px",
                  borderRadius: 10,
                  border: `2px solid ${t.border}`,
                  background: isDark ? "#1f2937" : "#f3f4f6",
                  color: t.textMuted,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviar}
                disabled={enviando}
                style={{
                  padding: "11px 30px",
                  borderRadius: 10,
                  border: "none",
                  background: "linear-gradient(135deg, #f59e0b, #d97706)",
                  color: "#fff",
                  cursor: enviando ? "wait" : "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: "0 4px 14px rgba(217,119,6,0.35)",
                  opacity: enviando ? 0.7 : 1,
                }}
              >
                {enviando ? "Enviando…" : "Enviar ticket"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
